import { z } from "zod"
import { LegacyCompatibleDiscountSchema } from "./discount"

export const ServiceWashSchema = z.object({
  id: z.guid(),
  name: z.string().min(1),
  price: z.number().positive(),
  description: z.string().nullable(),
  isBookable: z.boolean(),
  /** Estimated minutes of work; 15 unless the store sets otherwise. */
  durationMinutes: z.number().int().nonnegative(),
})

export const ServiceWashWithoutBookabilitySchema = ServiceWashSchema.omit({
  isBookable: true,
  durationMinutes: true,
})

export const ServiceWashSnapshotSchema =
  ServiceWashWithoutBookabilitySchema.omit({
    id: true,
  }).extend({
    originalWashId: z.guid(),
    snapshotWashId: z.guid(),
    /** Per-line discount in percent (0-100), or null if no discount. */
    laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
    isMandatory: z.boolean(),
    requiredPartIds: z.array(z.guid()),
    requiredManualServiceActionIds: z.array(z.guid()),
  })

export const CreateServiceWashSchema = ServiceWashSchema.omit({
  id: true,
}).extend({
  durationMinutes: z.number().int().nonnegative().default(15),
})
// Zod 4 applies .default() to missing keys even after .partial(), so the
// defaulted fields are made plain optional again: a PATCH that leaves them out
// must not reset them.
export const UpdateServiceWashSchema = CreateServiceWashSchema.partial().extend(
  {
    durationMinutes: z.number().int().nonnegative().optional(),
  },
)

export const WashAlternativeSchema = z.object({
  originalWashId: z.guid(),
  snapshotWashId: z.guid(),
  name: z.string().min(1),
  price: z.number().nonnegative(),
  /** Per-line discount in percent (0-100), or null if no discount. */
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  description: z.string().nullable(),
  isRecommended: z.boolean(),
  requiredPartIds: z.array(z.guid()),
  requiredManualServiceActionIds: z.array(z.guid()),
})

export const WashAlternativesGroupSchema = z.object({
  id: z.guid(),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
  alternatives: z.array(WashAlternativeSchema).min(1),
})

export type ServiceWash = z.infer<typeof ServiceWashSchema>
export type ServiceWashWithoutBookability = z.infer<
  typeof ServiceWashWithoutBookabilitySchema
>
export type ServiceWashSnapshot = z.infer<typeof ServiceWashSnapshotSchema>
export type CreateServiceWash = z.infer<typeof CreateServiceWashSchema>
export type UpdateServiceWash = z.infer<typeof UpdateServiceWashSchema>
export type WashAlternative = z.infer<typeof WashAlternativeSchema>
export type WashAlternativesGroup = z.infer<typeof WashAlternativesGroupSchema>
